import {useCallback, useEffect, useState} from 'react';

import {useRouter} from 'next/router';

import {getLinkData} from 'Application/utils/linkFieldUtils';
import {wait} from 'UI/utils/helpers';

import type {LinkField} from 'types/payload-types';

/**
 * A custom React hook that prepares all attributes and state necessary to render a menu item link.
 * This hook normalizes link data (href, rel, target, and download) via `getLinkData` and tracks whether the link
 * corresponds to the current route to expose an `isActive` flag. For anchor-type links, it also implements a robust
 * post-navigation scroll behavior: after route or hash changes, it repeatedly queries for the target element and
 * scrolls it into view as soon as it becomes available. This helps when content is rendered asynchronously and the
 * anchor target may not exist immediately after navigation.
 *
 * @param link The link configuration object with metadata such as label, type, and optional block/anchor id.
 * @returns An object containing `href`, `label`, `rel`, `target`, `isDownload`, and `isActive` for rendering a link.
 *
 * @example
 * ```tsx
 * const { href, label, rel, target, isDownload, isActive } = useMenuItem(navigationLink);
 *
 * return (
 *   <a href={href} rel={rel} target={target} download={isDownload} data-active={isActive}>
 *     {label}
 *   </a>
 * );
 * ```
 */
export const useMenuItem = (link: LinkField) => {
    const [isActive, setIsActive] = useState(false);
    const router = useRouter();
    const {href, isDownload, label, rel, target} = getLinkData(link);

    const asyncScrollIntoView = useCallback(async (url: string, shallow: boolean) => {
        if (link.linkType === 'anchor' && link.blockId && !shallow && href === url) {
            let element = document.querySelector(`[data-id="${link.blockId.replace('#', '')}"]`);

            do {
                // eslint-disable-next-line no-await-in-loop
                await wait(20);

                element = document.querySelector(`[data-id="${link.blockId.replace('#', '')}"]`);
                element?.scrollIntoView();
            } while (element === null);
        }
    }, [link, href]);

    const handleRouteChangeEnd = useCallback((url: string, {shallow}: {shallow: boolean}) => {
        void asyncScrollIntoView(url, shallow);
    }, [asyncScrollIntoView]);

    const handleHashChangeEnd = useCallback((url: string, {shallow}: {shallow: boolean}) => {
        void asyncScrollIntoView(url, shallow);
    }, [asyncScrollIntoView]);

    useEffect(() => {
        void asyncScrollIntoView(router.asPath, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        router.events.on('routeChangeComplete', handleRouteChangeEnd);
        router.events.on('hashChangeComplete', handleHashChangeEnd);

        if (!isActive && href === router.asPath) {
            setIsActive(true);
        }
        if (isActive && href !== router.asPath) {
            setIsActive(false);
        }

        return () => {
            router.events.off('routeChangeComplete', handleRouteChangeEnd);
            router.events.off('hashChangeComplete', handleHashChangeEnd);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [href, router, handleHashChangeEnd, handleRouteChangeEnd]);

    return {
        href,
        isActive,
        isDownload,
        label,
        rel,
        target
    };
};